// Generación procedural del contenido de cada tramo: filas de obstáculos y monedas.
// Funciones puras (solo dependen del generador aleatorio), fáciles de probar en Node.
import { CONFIG } from '../config.js';

const OB = CONFIG.obstacles;

// Volumen de colisión de cada tipo, en metros sobre el suelo del camino.
// 'deep' y 'pit' no tienen profundidad fija: la longitud la lleva cada pieza (piece.length).
export const OBSTACLES = {
  low: { depth: 0.6, bottom: 0, top: 0.9 }, // tronco bajo: saltar o cambiar de carril
  high: { depth: 0.5, bottom: 1.15, top: 2.0 }, // rama alta: deslizarse o cambiar de carril
  wall: { depth: 0.9, bottom: 0, top: 3.2 }, // roca: solo se esquiva cambiando de carril
  deep: { depth: 0, bottom: 0, top: 3.2 }, // columna de roca profunda: anula un carril durante metros
};

export const LANES = [-1, 0, 1];

/** Longitud máxima de un foso que se considera saltable (los demás anulan el carril). */
export const MAX_JUMP_PIT = OB.pitLength[1];

const range = (rand, [a, b]) => a + rand() * (b - a);

/** Elige un tipo según pesos { tipo: peso }. */
function weighted(rand, weights) {
  let total = 0;
  for (const k in weights) total += weights[k];
  let r = rand() * total;
  for (const k in weights) {
    r -= weights[k];
    if (r < 0) return k;
  }
  return Object.keys(weights)[0];
}

/** ¿La pieza deja el carril inutilizable durante varios metros (columna profunda o foso largo)? */
export function isClosure(piece) {
  return piece.type === 'deep' || (piece.type === 'pit' && piece.length > MAX_JUMP_PIT);
}

/** Extensión de una pieza a lo largo del tramo, relativa a la fila: [desde, hasta]. */
export function pieceExtent(piece) {
  if (piece.length) return [0, piece.length]; // fosos y columnas profundas empiezan en la fila
  const half = OBSTACLES[piece.type].depth / 2;
  return [-half, half];
}

function makePiece(rand, type, lane, span, d) {
  const piece = { type, lane, span };
  if (type === 'pit') {
    const [a, b] = OB.pitLength;
    piece.length = range(rand, [a, a + (b - a) * (0.5 + 0.5 * d)]);
  }
  return piece;
}

/**
 * Una fila de obstáculos: piezas que ocupan 1–3 carriles contiguos.
 * Garantía: siempre queda al menos un carril sin roca (superable). Los fosos de estas filas son
 * siempre saltables, así que un foso de 3 carriles también se supera.
 * d = dificultad 0..1
 */
export function pickRow(rand, d) {
  const type = weighted(rand, { low: 0.36, high: 0.32, wall: 0.18 + 0.2 * d, pit: OB.pitChance + 0.08 * d });
  let span;
  if (type === 'wall') span = rand() < 0.55 - 0.15 * d ? 1 : 2;
  else span = weighted(rand, { 1: 0.35, 2: 0.3, 3: 0.35 }) | 0;
  const lane = -1 + Math.floor(rand() * (4 - span));
  const pieces = [makePiece(rand, type, lane, span, d)];

  // Combinación: una segunda pieza en carriles libres (más frecuente con la dificultad).
  if (span < 3 && rand() < 0.15 + 0.45 * d) {
    const free = LANES.filter((l) => l < lane || l >= lane + span);
    // Carriles libres contiguos: si están separados (pieza en el centro), usamos solo uno.
    const contiguous = free.length === 2 && free[1] - free[0] === 1;
    const secondSpan = contiguous && rand() < 0.4 ? 2 : 1;
    const secondLane = secondSpan === 2 ? free[0] : free[Math.floor(rand() * free.length)];
    // Si la primera es roca, la segunda debe ser superable para no cerrar el paso.
    const secondType =
      type === 'wall'
        ? weighted(rand, { low: 0.4, high: 0.4, pit: 0.2 })
        : weighted(rand, { low: 0.35, high: 0.3, wall: 0.25, pit: 0.1 });
    pieces.push(makePiece(rand, secondType, secondLane, secondSpan, d));
  }
  return pieces;
}

/**
 * Bloqueo largo: columna de roca profunda (1 carril) o foso largo (1–2 carriles).
 * maxLength limita la longitud para que acabe antes de la esquina.
 */
export function pickClosure(rand, d, maxLength) {
  const deep = rand() < 0.55;
  const [a, b] = deep ? OB.deepLength : OB.longPitLength;
  const length = Math.min(maxLength, range(rand, [a, a + (b - a) * (0.4 + 0.6 * d)]));
  const span = !deep && rand() < 0.3 * d ? 2 : 1;
  const lane = -1 + Math.floor(rand() * (4 - span));
  return { type: deep ? 'deep' : 'pit', lane, span, length };
}

/**
 * Fila dentro de un bloqueo largo: solo troncos, ramas o fosos cortos en los carriles abiertos.
 * Así nunca hace falta cruzar el carril anulado para pasar.
 */
export function pickInside(rand, d, blocked) {
  const open = LANES.filter((l) => !blocked.has(l));
  const runs = [];
  for (const l of open) {
    const last = runs.at(-1);
    if (last && last.lane + last.span === l) last.span++;
    else runs.push({ lane: l, span: 1 });
  }
  const pieces = [];
  for (const run of runs) {
    if (pieces.length && rand() < 0.5) continue;
    const type = weighted(rand, { low: 0.45, high: 0.4, pit: 0.15 });
    pieces.push(makePiece(rand, type, run.lane, run.span, d));
  }
  return pieces;
}

/** Qué exige cada carril en una fila: 'free' | 'low' | 'high' | 'pit' (saltar) | 'wall' (no pasar). */
export function laneNeeds(pieces) {
  const needs = { [-1]: 'free', 0: 'free', 1: 'free' };
  for (const p of pieces) {
    const need = isClosure(p) || p.type === 'wall' ? 'wall' : p.type;
    for (let l = p.lane; l < p.lane + p.span; l++) needs[l] = need;
  }
  return needs;
}

/**
 * Filas de obstáculos a lo largo de un tramo. Cada fila: { s, end, pieces, closure? }.
 * La separación entre filas crece con la velocidad para dar siempre ~1 s de reacción.
 * Tras un bloqueo largo se deja la misma separación antes de la siguiente fila normal.
 */
export function generateRows(rand, { length, startDist, safeStart, endMargin, speedAt }) {
  const rows = [];
  const limit = length - endMargin;
  let s = safeStart + rand() * 4;
  let closure = null; // { lanes: Set, end }
  for (;;) {
    if (closure && s > closure.end - 1) {
      s = Math.max(s, closure.end + Math.max(8, speedAt(startDist + closure.end) * 0.75));
      closure = null;
    }
    if (s >= limit) break;
    const dist = startDist + s;
    const d = Math.min(1, dist / 2500);

    let row;
    if (closure) {
      row = { s, pieces: pickInside(rand, d, closure.lanes) };
    } else if (limit - s > OB.deepLength[0] && rand() < OB.closureChance + 0.08 * d) {
      const piece = pickClosure(rand, d, limit - s - 1);
      const lanes = new Set();
      for (let l = piece.lane; l < piece.lane + piece.span; l++) lanes.add(l);
      closure = { lanes, end: s + piece.length };
      row = { s, pieces: [piece], closure: true };
    } else {
      row = { s, pieces: pickRow(rand, d) };
    }
    row.end = s + Math.max(...row.pieces.map((p) => pieceExtent(p)[1]));
    rows.push(row);
    s += Math.max(10, speedAt(dist) * 0.95) + rand() * 8 * (1 - 0.5 * d);
  }
  return rows;
}

const COIN_Y = 0.9; // altura del centro de la moneda
const COIN_STEP = 2;
const POWERUP_Y = 1.1;
const GEM_CHANCE = 0.18;
export const GEM_VALUE = 5;

/** Carriles anulados por algún bloqueo largo entre a y b. */
function blockedLanes(rows, a, b) {
  const out = new Set();
  for (const r of rows) {
    for (const p of r.pieces) {
      if (!isClosure(p) || r.s > b || r.s + p.length < a) continue;
      for (let l = p.lane; l < p.lane + p.span; l++) out.add(l);
    }
  }
  return out;
}

function randomOpenLane(rand, blocked) {
  const open = LANES.filter((l) => !blocked.has(l));
  return open.length ? open[Math.floor(rand() * open.length)] : null;
}

/**
 * Objetos de un tramo: hileras de monedas en los huecos entre filas, arcos de monedas
 * sobre algunos troncos y fosos y, de vez en cuando, un potenciador en un hueco libre.
 * Nunca se ponen en un carril anulado por una columna profunda o un foso largo.
 * Devuelve { coins: [{ s, lane, y, gem }], powerUps: [{ s, lane, y, type }] }.
 */
export function generateItems(rand, rows, { length, startDist, powerUps }) {
  const coins = [];
  const found = [];
  // Huecos entre filas. De un bloqueo largo solo cuenta su comienzo: a su lado sí puede haber monedas.
  const gaps = [];
  let prev = 2;
  for (const r of rows) {
    gaps.push([prev, r.s - 0.5]);
    prev = r.closure ? r.s + 1 : r.end;
  }
  gaps.push([prev, length - 6]);

  // Potenciador: probabilidad proporcional a la longitud del tramo.
  let puGap = -1;
  if (startDist >= powerUps.minDistance && rand() < length / powerUps.every) {
    const candidates = [];
    for (let i = 0; i < gaps.length; i++) if (gaps[i][1] - gaps[i][0] >= 14) candidates.push(i);
    if (candidates.length) {
      puGap = candidates[Math.floor(rand() * candidates.length)];
      const [a, b] = gaps[puGap];
      const lane = randomOpenLane(rand, blockedLanes(rows, a, b));
      if (lane === null) puGap = -1;
      else found.push({ s: (a + b) / 2, lane, y: POWERUP_Y, type: weighted(rand, powerUps.weights) });
    }
  }

  for (let i = 0; i < gaps.length; i++) {
    const a = gaps[i][0] + 3.5;
    const b = gaps[i][1] - 3.5;
    if (i === puGap || b - a < 4 || rand() > 0.6) continue;
    const lane = randomOpenLane(rand, blockedLanes(rows, a, b));
    if (lane === null) continue;
    let n = 0;
    // A veces la moneda central de la hilera es una gema (vale GEM_VALUE).
    const gemAt = rand() < GEM_CHANCE ? Math.floor(Math.min(10, (b - a) / COIN_STEP + 1) / 2) : -1;
    for (let s = a; s <= b && n < 10; s += COIN_STEP, n++) coins.push({ s, lane, y: COIN_Y, gem: n === gemAt });
  }
  // Arcos de monedas sobre troncos bajos y fosos saltables: premio por saltar en vez de esquivar.
  for (const row of rows) {
    for (const p of row.pieces) {
      const isPit = p.type === 'pit' && !isClosure(p);
      if (!(p.type === 'low' || isPit) || rand() > (isPit ? 0.5 : 0.35)) continue;
      const lane = p.lane + Math.floor(rand() * p.span);
      const center = isPit ? row.s + p.length / 2 : row.s;
      const gem = rand() < GEM_CHANCE;
      for (const o of [-3, -1.5, 0, 1.5, 3]) {
        coins.push({ s: center + o, lane, y: COIN_Y + 1.5 * (1 - (o / 3.6) ** 2), gem: gem && o === 0 });
      }
    }
  }
  return { coins, powerUps: found };
}
